/**
 * Monitoring visit status — single source of truth (API Title Case values).
 */

export const VISIT_STATUS = {
  DRAFT: "Draft",
  PLANNED: "Planned",
  IN_PROGRESS: "In Progress",
  FINALIZED: "Finalized",
  CANCELLED: "Cancelled",
} as const;

export const VISIT_STATUS_VALUES = Object.values(VISIT_STATUS);

export type VisitStatus = (typeof VISIT_STATUS)[keyof typeof VISIT_STATUS];

const canonicalize = (value: string): string =>
  value.toLowerCase().replace(/[\s/\-_]+/g, "");

const STATUS_BY_KEY: Record<string, VisitStatus> = Object.values(
  VISIT_STATUS,
).reduce(
  (acc, value) => {
    acc[canonicalize(value)] = value;
    return acc;
  },
  {} as Record<string, VisitStatus>,
);

export const tryNormalizeVisitStatus = (status: string): VisitStatus | null => {
  if (!status?.trim()) {
    return null;
  }
  if (VISIT_STATUS_VALUES.includes(status as VisitStatus)) {
    return status as VisitStatus;
  }
  return STATUS_BY_KEY[canonicalize(status)] ?? null;
};

export const normalizeVisitStatus = (status: string): VisitStatus =>
  tryNormalizeVisitStatus(status) ?? VISIT_STATUS.DRAFT;

/** Strict compare: unknown status never matches (no Draft default). */
export const isVisitStatus = (status: string, target: VisitStatus): boolean =>
  tryNormalizeVisitStatus(status) === target;

export const isAnyVisitStatus = (
  status: string,
  ...targets: VisitStatus[]
): boolean => {
  const normalized = tryNormalizeVisitStatus(status);
  return normalized !== null && targets.includes(normalized);
};
